// =====================================================================
// Faz AV (2026-05-07) — Toplu Ürün CSV Upsert
// =====================================================================
// Faz AU müşteri import pattern'i ile aynı 3-adım wizard.
// Ürün için fark: SKU varsa UPDATE (diff göster), yoksa INSERT.
//
// Endpoint:
//   POST /api/admin/products/bulk-import/preview
//   POST /api/admin/products/bulk-import/confirm
//   GET  /api/admin/products/bulk-import/template
// =====================================================================
import React, { useState } from "react";
import {
  Box, Card, Typography, Grid, Chip, Table, TableHead, TableRow, TableCell,
  TableBody, Alert, Button, TextField, LinearProgress,
  Stepper, Step, StepLabel
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DownloadIcon from "@mui/icons-material/Download";
import CheckIcon from "@mui/icons-material/Check";
import RefreshIcon from "@mui/icons-material/Refresh";
import API from "../services/api";
import toast from "react-hot-toast";

// CSV parse — Faz AU ile aynı
function parseCsvLine(line) {
  const result = [];
  let current = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (c === '"') {
      if (inQuotes && line[i + 1] === '"') { current += '"'; i++; }
      else { inQuotes = !inQuotes; }
    } else if (c === "," && !inQuotes) {
      result.push(current); current = "";
    } else {
      current += c;
    }
  }
  result.push(current);
  return result.map(s => s.trim());
}

function parseCsv(text) {
  if (text.charCodeAt(0) === 0xFEFF) text = text.slice(1);
  const lines = text.split(/\r?\n/).filter(l => l.trim().length > 0);
  if (lines.length < 2) return { headers: [], rows: [] };
  const headers = parseCsvLine(lines[0]).map(h => h.toLowerCase());
  const rows = [];
  for (let i = 1; i < lines.length; i++) {
    const fields = parseCsvLine(lines[i]);
    const row = {};
    headers.forEach((h, idx) => { row[h] = fields[idx] || ""; });
    rows.push(row);
  }
  return { headers, rows };
}

const STEPS = ["1. CSV Yükle", "2. Önizle (Diff)", "3. Onayla → Upsert"];

const fmtMoney = (v) => Number(v || 0).toLocaleString("tr-TR", { minimumFractionDigits: 0, maximumFractionDigits: 2 }) + " ₺";

export default function BulkProductImport() {
  const [step, setStep] = useState(0);
  const [csvText, setCsvText] = useState("");
  const [parsedRows, setParsedRows] = useState([]);
  const [previewData, setPreviewData] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleFileUpload = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => setCsvText(e.target.result);
    reader.readAsText(file, "UTF-8");
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) handleFileUpload(file);
  };

  const downloadTemplate = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch("https://api.miksanplastik.com.tr/api/admin/products/bulk-import/template", {
        headers: { Authorization: `Bearer ${token}` }
      });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "miksan_product_template.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Template indirilemedi: " + e.message);
    }
  };

  const goToPreview = async () => {
    if (!csvText.trim()) { toast.error("CSV boş"); return; }
    const parsed = parseCsv(csvText);
    if (parsed.rows.length === 0) { toast.error("CSV satır içermiyor"); return; }
    if (!parsed.headers.includes("sku")) { toast.error('"sku" kolonu zorunlu'); return; }

    setParsedRows(parsed.rows);
    setLoading(true);
    try {
      const r = await API.post("/api/admin/products/bulk-import/preview", { rows: parsed.rows });
      setPreviewData(r.data);
      setStep(1);
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  };

  const confirmImport = async () => {
    setLoading(true);
    try {
      const r = await API.post("/api/admin/products/bulk-import/confirm", { rows: parsedRows });
      setImportResult(r.data);
      setStep(2);
      toast.success(`✓ ${r.data.inserted} eklendi, ${r.data.updated} güncellendi`);
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setStep(0);
    setCsvText("");
    setParsedRows([]);
    setPreviewData(null);
    setImportResult(null);
  };

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      <Typography variant="h4" fontWeight={800} mb={1}>Toplu Ürün Import</Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        SKU varsa update, yoksa insert. Stok/fiyat toplu güncelleme için ideal.
      </Typography>

      <Stepper activeStep={step} sx={{ mb: 4 }}>
        {STEPS.map(s => <Step key={s}><StepLabel>{s}</StepLabel></Step>)}
      </Stepper>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* ADIM 1 */}
      {step === 0 && (
        <Card sx={{ p: 3, borderRadius: 3 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography variant="h6" fontWeight={700}>CSV Hazırla</Typography>
            <Button startIcon={<DownloadIcon />} onClick={downloadTemplate} variant="outlined" size="small">
              Şablon İndir
            </Button>
          </Box>

          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>Format:</strong> Zorunlu: <code>sku, name, price, stock</code>.
            Opsiyonel: <code>category_id, brand, description, min_stock, unit</code>.
            <br/>
            <strong>SKU primary key:</strong> Aynı SKU sistemde varsa <strong>UPDATE</strong> olur (eski değerler değişir),
            yoksa <strong>INSERT</strong> olur (yeni ürün eklenir).
          </Alert>

          <Box
            onDrop={handleDrop}
            onDragOver={e => e.preventDefault()}
            sx={{
              border: "2px dashed #cbd5e1", borderRadius: 3, p: 4,
              textAlign: "center", bgcolor: "#f8fafc", mb: 2,
              cursor: "pointer", "&:hover": { borderColor: "#1565C0", bgcolor: "#eff6ff" }
            }}
            onClick={() => document.getElementById("csv-product-input").click()}
          >
            <CloudUploadIcon sx={{ fontSize: 48, color: "#94a3b8", mb: 1 }} />
            <Typography fontWeight={600}>CSV dosyasını buraya sürükle veya tıkla</Typography>
            <input
              id="csv-product-input" type="file" accept=".csv,text/csv"
              style={{ display: "none" }}
              onChange={(e) => e.target.files[0] && handleFileUpload(e.target.files[0])}
            />
          </Box>

          <TextField
            fullWidth multiline rows={8}
            label="Veya CSV içeriğini yapıştır"
            value={csvText}
            onChange={e => setCsvText(e.target.value)}
            placeholder={"sku,name,price,stock\nKAP-50-60,Çöp Poşeti 50x60,45.00,500"}
            sx={{ "& textarea": { fontFamily: "monospace", fontSize: 12 } }}
          />

          <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button onClick={() => setCsvText("")}>Temizle</Button>
            <Button variant="contained" onClick={goToPreview} disabled={!csvText.trim() || loading}>
              Önizle →
            </Button>
          </Box>
        </Card>
      )}

      {/* ADIM 2: Preview + DIFF */}
      {step === 1 && previewData && (
        <>
          <Grid container spacing={2} mb={3}>
            <Grid item xs={6} md={2.4}>
              <Card sx={{ p: 2, borderRadius: 3 }}>
                <Typography variant="caption" color="text.secondary">TOPLAM</Typography>
                <Typography variant="h4" fontWeight={800}>{previewData.summary.total}</Typography>
              </Card>
            </Grid>
            <Grid item xs={6} md={2.4}>
              <Card sx={{ p: 2, borderRadius: 3, bgcolor: "#f0fdf4" }}>
                <Typography variant="caption" color="text.secondary" fontWeight={700}>YENİ EKLENECEK</Typography>
                <Typography variant="h4" fontWeight={800} color="#10b981">{previewData.summary.inserts}</Typography>
              </Card>
            </Grid>
            <Grid item xs={6} md={2.4}>
              <Card sx={{ p: 2, borderRadius: 3, bgcolor: "#eff6ff" }}>
                <Typography variant="caption" color="text.secondary" fontWeight={700}>GÜNCELLENECEK</Typography>
                <Typography variant="h4" fontWeight={800} color="#3b82f6">{previewData.summary.updates}</Typography>
              </Card>
            </Grid>
            <Grid item xs={6} md={2.4}>
              <Card sx={{ p: 2, borderRadius: 3, bgcolor: "#f8fafc" }}>
                <Typography variant="caption" color="text.secondary" fontWeight={700}>DEĞİŞMEYECEK</Typography>
                <Typography variant="h4" fontWeight={800} color="#94a3b8">{previewData.summary.no_change}</Typography>
              </Card>
            </Grid>
            <Grid item xs={6} md={2.4}>
              <Card sx={{ p: 2, borderRadius: 3, bgcolor: "#fef2f2" }}>
                <Typography variant="caption" color="text.secondary" fontWeight={700}>HATA</Typography>
                <Typography variant="h4" fontWeight={800} color="#ef4444">{previewData.summary.errors}</Typography>
              </Card>
            </Grid>
          </Grid>

          {/* Anlamlı update'ler — diff göster (eski → yeni) */}
          {previewData.meaningful_updates.length > 0 && (
            <Card sx={{ borderRadius: 3, mb: 3, overflow: "hidden" }}>
              <Box sx={{ p: 2, bgcolor: "#eff6ff", borderBottom: "1px solid #dbeafe" }}>
                <Typography variant="h6" fontWeight={700}>📝 Değişecek Ürünler (ilk 10)</Typography>
                <Typography variant="caption" color="text.secondary">
                  Bu ürünler güncellenecek — eski değerden yeni değere değişim
                </Typography>
              </Box>
              <Table size="small">
                <TableHead sx={{ bgcolor: "#f8fafc" }}>
                  <TableRow>
                    <TableCell><strong>SKU</strong></TableCell>
                    <TableCell><strong>Ürün</strong></TableCell>
                    <TableCell><strong>Değişiklikler</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {previewData.meaningful_updates.map((r, i) => (
                    <TableRow key={i} hover>
                      <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>{r.sku}</TableCell>
                      <TableCell sx={{ fontSize: 13, fontWeight: 600 }}>{r.name}</TableCell>
                      <TableCell>
                        {r.changes && Object.entries(r.changes).map(([field, diff]) => (
                          <Box key={field} sx={{ fontSize: 12, mb: 0.3 }}>
                            <strong>{field === "price" ? "💰 Fiyat" : field === "stock" ? "📦 Stok" : field}:</strong>{" "}
                            <Chip
                              label={field === "price" ? fmtMoney(diff.from) : String(diff.from)}
                              size="small"
                              sx={{ height: 18, fontSize: 10, bgcolor: "#fee2e2", color: "#dc2626", textDecoration: "line-through" }}
                            />
                            {" → "}
                            <Chip
                              label={field === "price" ? fmtMoney(diff.to) : String(diff.to)}
                              size="small"
                              sx={{ height: 18, fontSize: 10, bgcolor: "#dcfce7", color: "#16a34a", fontWeight: 700 }}
                            />
                          </Box>
                        ))}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}

          {/* Yeni eklenecekler */}
          {previewData.preview.filter(r => r.is_insert).length > 0 && (
            <Card sx={{ borderRadius: 3, mb: 3, overflow: "hidden" }}>
              <Box sx={{ p: 2, bgcolor: "#f0fdf4", borderBottom: "1px solid #bbf7d0" }}>
                <Typography variant="h6" fontWeight={700}>✨ Yeni Eklenecek (preview ilk 15)</Typography>
              </Box>
              <Table size="small">
                <TableHead sx={{ bgcolor: "#f8fafc" }}>
                  <TableRow>
                    <TableCell><strong>SKU</strong></TableCell>
                    <TableCell><strong>İsim</strong></TableCell>
                    <TableCell align="right"><strong>Fiyat</strong></TableCell>
                    <TableCell align="right"><strong>Stok</strong></TableCell>
                    <TableCell><strong>Marka</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {previewData.preview.filter(r => r.is_insert).slice(0, 15).map((r, i) => (
                    <TableRow key={i} hover>
                      <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>{r.sku}</TableCell>
                      <TableCell sx={{ fontSize: 13 }}>{r.name}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: "#10b981" }}>{fmtMoney(r.price)}</TableCell>
                      <TableCell align="right">{r.stock}</TableCell>
                      <TableCell sx={{ fontSize: 12 }}>{r.brand || "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}

          {previewData.errors.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <strong>Hatalı satırlar (ilk 20):</strong>
              <Box component="ul" sx={{ m: 0, pl: 2, fontSize: 12 }}>
                {previewData.errors.slice(0, 10).map((e, i) => (
                  <li key={i}><code>{e.sku || "(boş)"}</code> — {e.errors.join(", ")}</li>
                ))}
              </Box>
            </Alert>
          )}

          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Button onClick={reset} startIcon={<RefreshIcon />}>← Yeniden Başla</Button>
            <Button
              variant="contained" color="success" startIcon={<CheckIcon />}
              onClick={confirmImport}
              disabled={loading || (previewData.summary.inserts + previewData.summary.updates) === 0}
            >
              {previewData.summary.inserts} Yeni + {previewData.summary.updates} Güncelle
            </Button>
          </Box>
        </>
      )}

      {/* ADIM 3 */}
      {step === 2 && importResult && (
        <Card sx={{ p: 4, borderRadius: 3, textAlign: "center" }}>
          <CheckIcon sx={{ fontSize: 64, color: "#10b981", mb: 2 }} />
          <Typography variant="h5" fontWeight={800} mb={1}>İmport Tamamlandı</Typography>

          <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mb: 3 }}>
            <Card sx={{ p: 2, bgcolor: "#f0fdf4" }}>
              <Typography variant="caption">Yeni Eklendi</Typography>
              <Typography variant="h4" fontWeight={800} color="#10b981">{importResult.inserted}</Typography>
            </Card>
            <Card sx={{ p: 2, bgcolor: "#eff6ff" }}>
              <Typography variant="caption">Güncellendi</Typography>
              <Typography variant="h4" fontWeight={800} color="#3b82f6">{importResult.updated}</Typography>
            </Card>
            <Card sx={{ p: 2, bgcolor: "#fffbeb" }}>
              <Typography variant="caption">Atlandı</Typography>
              <Typography variant="h4" fontWeight={800} color="#f59e0b">{importResult.skipped}</Typography>
            </Card>
          </Box>

          {importResult.errors.length > 0 && (
            <Alert severity="info" sx={{ mb: 3, textAlign: "left" }}>
              <Typography fontWeight={600} mb={1}>Atlanan satırlar:</Typography>
              <Box component="ul" sx={{ m: 0, pl: 2, fontSize: 11 }}>
                {importResult.errors.map((e, i) => (
                  <li key={i}><code>{e.sku}</code> — {e.reason}</li>
                ))}
              </Box>
            </Alert>
          )}

          <Button variant="contained" onClick={reset}>Yeni Import Başlat</Button>
        </Card>
      )}
    </Box>
  );
}
